import type { EChartsOption } from "echarts";
import { useContext, useRef, useState } from "react";
import { Box, CircularProgress, Container } from "@mui/material";
import DashboardCardHeader from "./DashboardCardHeader";
import { RopChart } from "../../../main/components/RopChart";
import { DashboardContext } from "../context/DashboardContexProvider";
import { BaseErrorCard } from "../models/dados-indicadores.model";
import { EmptyTable } from "../../clients/components/EmptyState";
import { CardTypesEnum } from "../types";
import { getCardIsLoading } from "./TextCardContent";

export type ParetoChartValues = {
  code: string;
  amount: number;
  percentage: number;
  name: string;
    duration?: string;
  area?: string;
  descricao?: string;
};

export type ParetoChartProps = {
  cardId?: string;
  title?: string;
  lastUpdate: Date;
  cardType: number;
  data: {
    alarms: ParetoChartValues[];
    cardError: BaseErrorCard;
  };
};
export default function ParetoChart({
  cardId,
  title,
  data,
  lastUpdate,
  cardType,
}: ParetoChartProps) {
  const [isMouseIn, setIsMouseIn] = useState(false);
  const {
    cardsDisplayableColorProps,
    setCardsDisplayableColorProps,
    cardIsLoading,
    requestParamCards,
  } = useContext(DashboardContext);

  const [eventosQntdColor, setEventosQntdColor] = useState<string>(
    cardsDisplayableColorProps.pareto.enventosQntdColor
  );
  const [porcentagemColor, setPorcentagemColor] = useState<string>(
    cardsDisplayableColorProps.pareto.porcentagemColor
  );

  const [openModal, setopenModal] = useState<boolean>(false);

  const handleConfirmModal = () => {
    setCardsDisplayableColorProps({
      ...cardsDisplayableColorProps,
      pareto: {
        enventosQntdColor: eventosQntdColor,
        porcentagemColor: porcentagemColor,
      },
    });
    setopenModal(false);
  };
  const percentageValues = data.alarms.map((item, index) => {
    const previousValues = data.alarms.slice(0, index);
    const previousPercentageSum = previousValues.reduce(
      (acc, item) => acc + item.percentage,
      0
    );
    return (item.percentage + previousPercentageSum).toFixed(2);
  });
  const alarmValues = data.alarms.map((item) => item.amount);
  const xLabels = data.alarms.map((item) => item.code);
  const legendData = ["Quantidade de eventos", "Porcentagem"];
  const option: EChartsOption = {
    title: {
      text: "",
      textStyle: {
        fontSize: 14,
        fontWeight: "bold",
      },
    },
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        crossStyle: {
          color: "#999",
        },
      },
      formatter: function (params: any) {
        var alarmMessage = data.alarms.find((alarm) => alarm.code == params[0].name)?.name

        let qntdEventosVPtBr: string;

        if (typeof params[0].value !== "number") {
          qntdEventosVPtBr = `${params[0].value}`;
        } else {
          qntdEventosVPtBr = params[0].value.toLocaleString("pt-BR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          });
        }

        let porcentagemVPtBr: string;

          porcentagemVPtBr = Number(params[1].value).toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });

        return `<b>${params[0].name}</b> <br/>${alarmMessage} <br/>${params[0].seriesName}: <b>${qntdEventosVPtBr}</b> <br/>${params[1].seriesName}: <b>${porcentagemVPtBr} %</b>`
      },
      confine: true,
    },

    legend: {
      data: legendData,
      left: "right",
    },
    xAxis: [
      {
        type: "category",
        data: xLabels,
        axisPointer: {
          type: "shadow",
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        name: "QTD",
      },
      {
        type: "value",
        name: "Acumulado",
        axisLabel: {
          formatter: "{value} %",
        },
      },
    ],
    series: [
      {
        name: "Quantidade de eventos",
        type: "bar",
        data: alarmValues,
        itemStyle: {
          color: eventosQntdColor,
        },
      },
      {
        name: "Porcentagem",
        yAxisIndex: 1,
        type: "line",
        data: percentageValues,
        itemStyle: {
          color: porcentagemColor,
        },
      },
    ],
  };

  const elementRef = useRef(null);
  const headerTitle = title ? "Ocorrências de alarmes" : "";
  const timeout = 120;
  const now = new Date();
  const delay = lastUpdate ? (now.getTime() - lastUpdate.getTime()) / 1000 : 0;
  const hasDelay = delay > timeout;

  const hasData = alarmValues.length > 0 || percentageValues.length > 0;

  return (
    <>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseOver={() => setIsMouseIn(true)}
        onMouseLeave={() => setIsMouseIn(false)}
      >
        <DashboardCardHeader
          cardError={data.cardError}
          hasDelay={hasDelay}
          lastUpdate={lastUpdate}
          cardId={cardId}
          elementRef={elementRef}
          isMouseIn={isMouseIn}
          title={headerTitle}
          titleFontSize={21}
          openColorsModal={openModal}
          setOpenColorsModal={setopenModal}
          onConfirmColorsModal={handleConfirmModal}
          colorsOptions={[
            {
              title: "Quantidade de eventos",
              value: eventosQntdColor,
              setValue: setEventosQntdColor,
            },
            {
              title: "Porcentagem",
              value: porcentagemColor,
              setValue: setPorcentagemColor,
            },
          ]}
          colorButton
          cardType={CardTypesEnum.Pareto}
          hasFilter
        />
        {!getCardIsLoading(cardType, cardIsLoading, requestParamCards) ? (
          hasData ? (
            <Container
              ref={elementRef}
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "90%",
                width: "100%",
              }}
            >
              <RopChart option={option} />
            </Container>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <EmptyTable title="Nenhum dado encontrado!" />
            </Box>
          )
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </>
  );
}
